import { useClientRole } from "@/components/client-role";
import { PageHeader } from "@/components/dashboard/page-header";
import { SectionLabel } from "@/components/dashboard/section-label";
import { cn } from "@/lib/utils";
import { useState } from "react";

// Admin-only audit trail — who changed what, most recent first. One entry per admin mutation (the same
// audit_log every write already produces). Representative data here; devs wire the live feed.

type Cat = "role" | "candidate" | "access";
type Entry = { day: string; who: string; cat: Cat; verb: string; object: string; time: string };

const ENTRIES: Entry[] = [
  {
    day: "Today",
    who: "Priya Shah",
    cat: "candidate",
    verb: "moved",
    object: "Jane Okafor → Final panel",
    time: "11:20",
  },
  {
    day: "Today",
    who: "Alex Morgan",
    cat: "role",
    verb: "launched the pipeline for",
    object: "Chief Technology Officer",
    time: "09:48",
  },
  {
    day: "Today",
    who: "Priya Shah",
    cat: "candidate",
    verb: "logged a final-panel round for",
    object: "Marcus Feld",
    time: "09:12",
  },
  {
    day: "Yesterday",
    who: "Alex Morgan",
    cat: "access",
    verb: "invited",
    object: "Jordan Lee as Viewer",
    time: "17:35",
  },
  {
    day: "Yesterday",
    who: "Priya Shah",
    cat: "candidate",
    verb: "added",
    object: "Devon Cole to VP Engineering",
    time: "16:02",
  },
  {
    day: "Yesterday",
    who: "Alex Morgan",
    cat: "role",
    verb: "put on hold",
    object: "Director of Marketing",
    time: "14:19",
  },
  {
    day: "Mon 30 Jun",
    who: "Priya Shah",
    cat: "candidate",
    verb: "marked",
    object: "Tom Bradley as Placed",
    time: "16:40",
  },
  {
    day: "Mon 30 Jun",
    who: "Alex Morgan",
    cat: "access",
    verb: "changed",
    object: "Casey Tan to Viewer",
    time: "10:05",
  },
];

const FILTERS: { v: Cat | "all"; label: string }[] = [
  { v: "all", label: "All" },
  { v: "role", label: "Roles" },
  { v: "candidate", label: "Candidates" },
  { v: "access", label: "Access" },
];

// No category colour here: blue/green/amber are the STATUS affordances (in progress / win / paused), so
// tinting a *category* (role/candidate/access) with them reads as a status it doesn't have. The filter
// label + the entry text carry the category; the trail stays deliberately neutral.

function initials(name: string) {
  const p = name.split(/\s+/).filter(Boolean);
  return ((p[0]?.[0] ?? "") + (p[1]?.[0] ?? "")).toUpperCase() || "?";
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "rounded-full border px-3 py-1 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
        active
          ? "border-transparent bg-foreground text-background shadow-sm"
          : "border-border bg-card text-muted-foreground hover:border-foreground/30 hover:bg-foreground/[0.04] hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

/** Activity — the audit trail, one entry per admin mutation, most recent first. Its own destination in the
 *  admin nav: an operational log, deliberately not buried under a "settings" catch-all. */
export function ActivityPage() {
  const isAdmin = useClientRole() === "admin";
  const [filter, setFilter] = useState<Cat | "all">("all");
  // The client sees a filtered, client-safe trail — progress on their searches (roles + candidates), never
  // the internal access / user-management events. OW staff see the full audit log.
  const visible = isAdmin ? ENTRIES : ENTRIES.filter((e) => e.cat !== "access");
  const filters = isAdmin ? FILTERS : FILTERS.filter((f) => f.v !== "access");
  const shown = filter === "all" ? visible : visible.filter((e) => e.cat === filter);
  const days = [...new Set(shown.map((e) => e.day))];

  return (
    <div className="animate-fade-up space-y-6">
      <PageHeader
        title="Activity"
        description={
          isAdmin
            ? "Every change your team made — most recent first."
            : "Everything that's happened across your searches — most recent first."
        }
      />

      <div className="flex flex-wrap items-center gap-1.5">
        {filters.map((f) => (
          <Chip key={f.v} active={filter === f.v} onClick={() => setFilter(f.v)}>
            {f.label}
          </Chip>
        ))}
      </div>

      {days.length === 0 ? (
        <div className="rounded-xl border border-dashed bg-card p-8 text-center text-sm text-muted-foreground">
          No activity in this view.
        </div>
      ) : (
        <div className="space-y-5">
          {days.map((day) => (
            <div key={day}>
              <SectionLabel>{day}</SectionLabel>
              <div className="overflow-hidden rounded-xl border bg-card">
                <div className="divide-y">
                  {shown
                    .filter((e) => e.day === day)
                    .map((e) => (
                      <div key={`${e.time}-${e.object}`} className="flex items-center gap-3.5 px-4 py-3">
                        <span className="grid size-8 shrink-0 place-items-center rounded-full bg-secondary text-[11px] font-semibold text-secondary-foreground">
                          {initials(e.who)}
                        </span>
                        <p className="min-w-0 flex-1 text-sm text-foreground">
                          <span className="font-medium">{e.who}</span>{" "}
                          <span className="text-muted-foreground">{e.verb}</span>{" "}
                          <span className="font-medium">{e.object}</span>
                        </p>
                        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{e.time}</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
